import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Box, 
  Avatar, 
  Typography, 
  Drawer, 
  IconButton, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

interface HeaderProps {
  onLogout?: () => void;
  onViewChange?: (view: string) => void;
  isAdmin?: boolean;
}

const Header: React.FC<HeaderProps> = ({ onLogout, onViewChange, isAdmin = false }) => {
  const [userInfo, setUserInfo] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleCancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  const handleConfirmLogout = () => {
    setLogoutDialogOpen(false);
    if (onLogout) {
      onLogout();
    }
  };

  useEffect(() => {
    // Get user info from localStorage
    const storedUserInfo = localStorage.getItem('user_info');

    if (storedUserInfo) {
      try {
        const parsed = JSON.parse(storedUserInfo);

        setUserInfo(parsed);
      } catch (error) {
        console.error('Error parsing user info:', error);
      }
    } else {

    }
  }, []);

  // Get initials for avatar
  const getInitials = (name: string) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
    <AppBar 
      position="sticky" 
      sx={{ 
        background: '#e8e8e8',
        width: '100%',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
      }}
    >
      <Toolbar 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          minHeight: 64,
          px: 2,
          width: '100%'
        }}
      >
        {/* Logo Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={toggleDrawer}
            sx={{ 
              color: '#000',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.05)'
              }
            }}
          >
            <MenuIcon sx={{ fontSize: 28 }} />
          </IconButton>
          
          <img 
            src="/logo.png" 
            alt="Nike" 
            style={{ height: '40px', width: 'auto' }}
          />
          
          <Typography 
            variant="h4" 
            sx={{ 
              fontFamily: "'Impact', 'Arial Black', 'Haettenschweiler', 'Charcoal', sans-serif",
              fontWeight: 900,
              color: '#000',
              letterSpacing: 'normal',
              fontSize: '28px',
              textTransform: 'uppercase',
              lineHeight: 1,
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              wordSpacing: '8px'
            }}
          >
            Connected Products Platform
          </Typography>
        </Box>

        {/* Navigation Links */}
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flex: 1, justifyContent: 'center' }}>
          {/* Navigation links removed but functionality preserved */}
        </Box>
          
        {/* User Section - Compact in corner */}
        <Box sx={{ 
          borderLeft: '1px solid rgba(0, 0, 0, 0.1)', 
          pl: 2, 
          ml: 2,
          display: 'flex',
          gap: 1.5,
          alignItems: 'center'
        }}>
          {/* User Info Display - Compact */}
          {userInfo && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar 
                sx={{ 
                  bgcolor: '#000',
                  width: 40,
                  height: 40,
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
                  fontSize: '14px',
                  fontWeight: 600
                }}
              >
                {getInitials(userInfo.name)}
              </Avatar>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
                    color: '#000',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: 1.3
                  }}
                >
                  {userInfo.name || userInfo.email?.split('@')[0].split('.').map((p: string) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ') || 'User'}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
                    color: '#666',
                    fontWeight: 400,
                    fontSize: '12px'
                  }}
                >
                  {userInfo.email}
                </Typography>
              </Box>
            </Box>
          )}

          <Button 
            variant="outlined"
            onClick={handleLogoutClick}
            sx={{ 
              '&:hover': { 
                background: 'rgba(33, 150, 243, 0.08)',
                borderColor: '#2196F3'
              },
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
              borderColor: '#2196F3',
              color: '#2196F3',
              fontWeight: 600,
              fontSize: '14px',
              transition: 'all 0.2s ease',
              textTransform: 'capitalize',
              cursor: 'pointer',
              px: 3,
              py: 0.8,
              borderRadius: '24px',
              borderWidth: '2px'
            }}
          >
            Logout
          </Button>
        </Box>
      </Toolbar>
    </AppBar>

    <Drawer
      anchor="left"
      open={drawerOpen}
      onClose={toggleDrawer}
      PaperProps={{
        sx: {
          width: 250,
          bgcolor: '#f5f5f5',
          boxShadow: '2px 0 8px rgba(0,0,0,0.15)'
        }
      }}
      sx={{
        zIndex: 1400
      }}
    >
      <Box sx={{ width: 250, pt: 3, pb: 2, px: 2 }}>
        {/* QR Redirect - Title only (not clickable) */}
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '20px',
            color: '#000',
            fontFamily: "'Impact', 'Arial Black', sans-serif",
            mb: 2,
            px: 2
          }}
        >
          QR Redirect
        </Typography>
        
        {/* Rules - Clickable navigation (All users) */}
        <Box
          onClick={() => {
            onViewChange?.('my-rules');
            setDrawerOpen(false);
          }}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'rgba(33, 150, 243, 0.08)',
              transform: 'translateX(4px)'
            }
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              color: '#000',
              fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
            }}
          >
            Rules
          </Typography>
        </Box>
        
        {/* Approvals - Clickable navigation (Admin only) */}
        {isAdmin && (
          <Box
            onClick={() => {
              onViewChange?.('approvals');
              setDrawerOpen(false);
            }}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: 'rgba(33, 150, 243, 0.08)',
                transform: 'translateX(4px)'
              }
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '16px',
                color: '#000',
                fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
              }}
            >
              Approvals
            </Typography>
          </Box>
        )}

        {/* Audit Log - Clickable navigation (Admin only) */}
        {isAdmin && (
          <Box
            onClick={() => {
              onViewChange?.('audit-log');
              setDrawerOpen(false);
            }}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: 'rgba(33, 150, 243, 0.08)',
                transform: 'translateX(4px)'
              }
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '16px',
                color: '#000',
                fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
              }}
            >
              Audit Log
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>

    {/* Logout Confirmation Dialog */}
    <Dialog
      open={logoutDialogOpen}
      onClose={handleCancelLogout}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          pb: 1,
          pt: 3,
          fontWeight: 600,
          fontSize: '18px',
          fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
        }}
      >
        Logout Confirmation
      </DialogTitle>
      
      <DialogContent sx={{ pb: 1, pt: 2 }}>
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#555',
            fontSize: 15,
            fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
          }}
        >
          Are you sure you want to logout?
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 3, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleCancelLogout}
          sx={{
            borderColor: '#9e9e9e',
            color: '#666',
            fontWeight: 600,
            px: 3,
            py: 1,
            textTransform: 'none',
            fontSize: 15,
            fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
            '&:hover': {
              borderColor: '#757575',
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmLogout}
          sx={{
            backgroundColor: '#2196F3',
            color: '#fff',
            fontWeight: 600,
            px: 3,
            py: 1,
            textTransform: 'none',
            fontSize: 15,
            fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
            '&:hover': {
              backgroundColor: '#1976D2'
            }
          }}
        >
          Logout
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default Header;
